/**
 * Tournament Rules Tab (Campaign Variant)
 * Shows tournament rules, terms, and participation guidelines.
 * Reads from the campaigns store.
 */

import React, { memo, useState, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import LoadingIndicator from 'shared/components/LoadingIndicator';
import Image from 'shared/components/Image';
import RulesFormula from './FormulaRules';
import TermsInfoModal from './TermsInfoModal';
import TransWithFallback from 'shared/components/Translations/TransWrapper';
import { formatCurrencyValue } from 'shared/utils/helpers';
import { testIds } from 'core/testIds';
import {
  useSelectedCampaign,
  useAutoJoin,
  TOURNAMENTS_CAMPAIGN_TYPE,
  type ITermsModalData,
  type IParsedTournament,
  IGameModes,
} from 'features/campaigns';

import styles from './TournamentRulesTab.module.css';

interface RulesTabProps {
  campaignId?: string;
  isActive?: boolean;
}

const RulesTab: React.FC<RulesTabProps> = () => {
  const [modalData, setModalData] = useState<ITermsModalData>();
  const { t } = useTranslation();
  const campaign = useSelectedCampaign(TOURNAMENTS_CAMPAIGN_TYPE);

  const tournament = campaign as IParsedTournament | undefined;
  const autoJoin = useAutoJoin(TOURNAMENTS_CAMPAIGN_TYPE);

  const handleTermsClick = useCallback(() => {
    if (!tournament) return;
    setModalData({
      isOpen: true,
      title: t('tournaments.terms_and_conditions', 'Terms & Conditions'),
      content: tournament.terms || t('tournaments.default_terms', 'Standard tournament terms and conditions apply.'),
      tournamentId: tournament.id,
      tournamentName: tournament.name,
    });
  }, [tournament, t]);

  const minStakeInfo = useMemo(() => {
    if (!tournament) return null;
    return {
      amount: tournament.minStake || tournament.minBet || 0,
      currency: tournament.baseCurrency || tournament.currency || '',
      hasRequirement: !!(tournament.minStake || tournament.minBet),
    };
  }, [tournament]);

  if (!tournament) {
    return (
      <div className={styles['rulesWrapper']}>
        <div className={styles['loaderContainer']}>
          <LoadingIndicator show={true} absolute />
        </div>
        <div className={styles['contentContainer']} style={{ visibility: 'hidden' }}>
          <div />
        </div>
      </div>
    );
  }

  const showBuyBonusText = tournament.restrictions?.eligibleGameModes?.includes(IGameModes.BUY_BONUS);

  return (
    <div className={styles['rulesWrapper']}>
      {modalData?.isOpen && <TermsInfoModal modalData={modalData} setModalData={setModalData} />}
      <div className={styles['loaderContainer']} style={{ display: 'none' }}>
        <LoadingIndicator show={false} absolute />
      </div>
      <div className={styles['contentContainerWrapper']}>
        <div className={styles['contentContainer']} data-testid={testIds.campaigns.rulesContent(TOURNAMENTS_CAMPAIGN_TYPE)}>
        <div className={styles['textSection']}>
          <div>
            <div className={styles['title']}>
              <TransWithFallback i18nKey={'tournaments.rules.participation_rules'} />
            </div>
            <div className={styles['description']}>
              {!autoJoin && (
                <span>
                  <TransWithFallback
                    i18nKey="tournaments.rules.join_tournament_text"
                    components={{ span: <span className={styles['highlightedText']} /> }}
                  />
                </span>
              )}
              <TransWithFallback
                i18nKey="tournaments.rules.participating_game_text"
                components={{ span: <span className={styles['highlightedText']} /> }}
                values={{ minSpinNumber: tournament.minSpinNumber || 1 }}
              />
              {showBuyBonusText && <TransWithFallback i18nKey="tournaments.rules.buy_bonus_text" />}
              {minStakeInfo?.hasRequirement && (
                <span>
                  <TransWithFallback
                    i18nKey="tournaments.rules.min_participation_bet"
                    components={{ span: <span className={styles['highlightedText']} /> }}
                    values={{
                      minStake: formatCurrencyValue(minStakeInfo.amount, 100),
                      currency: minStakeInfo.currency,
                    }}
                  />
                </span>
              )}
            </div>
          </div>
          <div>
            <div className={styles['title']}>
              <TransWithFallback i18nKey={'tournaments.rules.scoring_rules'} />
            </div>
            <div className={styles['description']}>
              <RulesFormula
                formula={tournament.formula}
                currency={tournament.baseCurrency || ''}
                formulaParams={tournament.formulaParams}
              />
            </div>
          </div>
        </div>
        <Image
          onClick={handleTermsClick}
          src="/tournaments/icons/Info"
          manualAffix=".svg"
          alt="Tournament Terms and Conditions"
          className={styles['infoIcon']}
        />
      </div>
      </div>
    </div>
  );
};

export default memo(RulesTab);
